// frontend/src/hooks/useProject.ts — per-project metadata.
//
// Consumes the backend endpoint `GET /api/projects/:projectId`. The flow:
//   • Backend route → wire schema → Axios apiClient →
//     TanStack Query → this hook → consumers (ThemeProvider · useDisplayZone ·
//     the wizards' pre-fills · the frames' scope label).
//
// Returns ProjectSchema (member-read field set per ANATOMY §Doctrine: the field-
// exposure rule): id · name · display_name · type · color_primary ·
// color_on_primary · status · is_trial · trial_expires_at · default_currency ·
// timezone_iana · default_language.
//
// Default freshness (mount-based via global queryClient): stale on every
// mount, no window-focus refetch, no background polling. The /project key
// doesn't carry boot-essentials freshness (that's ['me']'s exclusive role).

import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { Project } from '@ems-app/schemas/projects/responses';
import { apiClient } from '../lib/apiClient.js';
import { ME_QUERY_KEY } from './useMe.js';

export const projectQueryKey = (projectId: string) => ['project', projectId] as const;

/**
 * The single reconciliation point for a `projects`-row write, from EITHER
 * management surface — the project-admin settings page (`/api/projects/:id/settings`)
 * OR the sysadmin platform portal (`/api/platform/projects/:id` + lifecycle + create).
 *
 * A project's identity/brand/status is denormalized into TWO client caches, so a
 * write that touches one leaves the other stale (the "switcher didn't update
 * until I refreshed" bug). This invalidates BOTH so every surface converges
 * immediately instead of waiting for the 60s `['me']` poll:
 *   - `['me']` — its `availableProjects` list feeds the ProjectSwitcher swatch +
 *     name + status badge, the select-project cards, and the blocked-page brand.
 *     (Its entitlement set also lags a platform module toggle, so refreshing it
 *     doubles as the nav-gating refresh.)
 *   - `['project', id]` — the active project's read that drives ThemeProvider's
 *     chrome colour + the page title. Omit `projectId` for create (no active read
 *     to refresh; the `['me']` list is what gains the row).
 *
 * Every project-writing mutation MUST call this. A future project surface
 * (warehouses, teams) inherits the rule by reusing this helper, not by
 * re-deriving the cache set. See FRONTEND-PATTERNS §Mutations.
 */
export function useInvalidateProjectViews(): (projectId?: string) => void {
  const qc = useQueryClient();
  return (projectId?: string) => {
    void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    if (projectId) void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
  };
}

async function fetchProject(projectId: string): Promise<Project> {
  const r = await apiClient.get<Project>(`/projects/${projectId}`);
  return r.data;
}

export function useProject(projectId: string): UseQueryResult<Project, Error> {
  return useQuery({
    queryKey: projectQueryKey(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: projectId.length > 0,
  });
}
